import type { AnyBlockConfigOnComponentOptions } from '#layers/block-engine/public';
import type { DisplayTemplateAst } from '#layers/data-model/types/display-template';
import type { FieldSelectionContext, IdFieldSelection, IdRelationQuery } from '#layers/data-model/types/field-selection';
import type { M2MStructure } from '#layers/data-model/types/relation';
import type { GlobalCollectionId, GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId } from '#layers/data-model/types/schema/ids';
import type {
	AnyResolvedField,
	ResolvedMonospaceCollection,
	ResolvedMonospaceSingleRelationField,
} from '#layers/data-model/types/schema/resolved-schema';
import { callOrReturn } from '#layers/base/public';
import { DisplayTemplateAstType } from '#layers/data-model/types/display-template';
import { getCollectionDefaultDisplayTemplate, getM2MDefaultDisplayTemplate } from '#layers/data-model/utils/display-template';
import { getFieldDisplayBlockOptions } from '#layers/data-model/utils/fields/field-display';
import { findCollectionFieldById } from '#layers/data-model/utils/fields/fields';
import { getM2MStructure, isM2M } from '#layers/data-model/utils/relation';
import { isPrimitiveField } from '#layers/data-model/utils/schema/collection';
import { RELATED_VALUES_DROPDOWN_MAX_ITEM_COUNT } from '#shared/constants/related-values';

export interface ResolvedFieldSelectionContext {
	getDisplayTemplateFieldSelection: (ast: DisplayTemplateAst, collection: ResolvedMonospaceCollection) => IdFieldSelection;
	getCollection: (id: GlobalCollectionId) => ResolvedMonospaceCollection | undefined;
	getRelationField: (fieldId: GlobalSingleRelationFieldId) => ResolvedMonospaceSingleRelationField | undefined;
	resolveDisplayBlock: (displayId: string) => Pick<AnyBlockConfigOnComponentOptions, 'display'> | undefined;
}

const MAX_DEPTH = 15;

function relationEntry(fieldId: GlobalSingleRelationFieldId, selection: IdFieldSelection | IdRelationQuery): IdFieldSelection[number] {
	return { [fieldId]: selection } as IdFieldSelection[number];
}

/**
 * Resolve the IdFieldSelection needed to display a single field.
 *
 * For primitive fields: always includes the field's own ID, plus any extra
 * fields declared by the display block's `fields()` config.
 * For relation fields: checks the display block's `fields()` config, falling
 * back to the opposite collection's display template.
 */
export function resolveFieldDisplayFieldSelection(
	field: AnyResolvedField,
	collection: ResolvedMonospaceCollection,
	ctx: ResolvedFieldSelectionContext,
): IdFieldSelection {
	return resolveFieldDisplayFieldSelectionRecursive(field, collection, ctx, new Set());
}

function resolveFieldDisplayFieldSelectionRecursive(
	field: AnyResolvedField,
	collection: ResolvedMonospaceCollection,
	ctx: ResolvedFieldSelectionContext,
	_visited: Set<GlobalCollectionId>,
): IdFieldSelection {
	if (isPrimitiveField(field)) {
		const result: IdFieldSelection = [field.id as GlobalPrimitiveFieldId];

		const { block, props: blockProps } = getFieldDisplayBlockOptions(field);
		const blockConfig = ctx.resolveDisplayBlock(block);
		const displayFields = blockConfig?.display?.fields;

		if (displayFields) {
			const extraFields = callOrReturn(displayFields, blockProps, ctx as FieldSelectionContext);
			const expanded = expandBareRelationFieldIds(extraFields, collection, ctx, _visited);
			result.push(...expanded);
		}

		return result;
	}

	const relFieldId = field.id as GlobalSingleRelationFieldId;
	const rawSelection = getDisplayBlockFieldSelection(relFieldId, ctx);
	return expandBareRelationFieldIds(rawSelection, collection, ctx, _visited);
}

export function getDisplayTemplateFieldSelection(
	ast: DisplayTemplateAst,
	collection: ResolvedMonospaceCollection,
	ctx: ResolvedFieldSelectionContext,
	_visited: Set<GlobalCollectionId> = new Set(),
): IdFieldSelection {
	if (!ast.content || ast.content.length === 0) return [];
	if (_visited.size >= MAX_DEPTH) return ['*'];

	const result: IdFieldSelection = [];

	for (const node of ast.content) {
		if (node.type !== DisplayTemplateAstType.Ref) continue;

		const { fieldId } = node.attrs;

		// Array fieldId = pre-resolved path (e.g. [relId, nestedRelId, primitiveId])
		if (Array.isArray(fieldId)) {
			const selection = fieldIdPathToSelection(fieldId, collection, ctx, _visited);

			if (selection) {
				result.push(selection);
			}

			continue;
		}

		const field = findCollectionFieldById(collection, fieldId);

		if (!field) continue;

		result.push(...resolveFieldDisplayFieldSelectionRecursive(field, collection, ctx, _visited));
	}

	return result;
}

/**
 * Get the field selection from a display block's fields() config.
 * Falls back to [relFieldId] (bare relation field ID) when no display block fields exist.
 */
function getDisplayBlockFieldSelection(
	relFieldId: GlobalSingleRelationFieldId,
	ctx: ResolvedFieldSelectionContext,
): IdFieldSelection {
	const field = ctx.getRelationField(relFieldId);

	if (!field) return [relFieldId];

	const { block, props: blockProps } = getFieldDisplayBlockOptions(field);
	const blockConfig = ctx.resolveDisplayBlock(block);
	const displayFields = blockConfig?.display?.fields;

	if (displayFields) {
		const result = callOrReturn(displayFields, blockProps, ctx as FieldSelectionContext);

		warnIfBareOppositeCollectionFields({ selection: result, relFieldId, field, blockId: block });

		return result;
	}

	// Default: bare relation field ID, triggers auto-expansion
	return [relFieldId];
}

/**
 * Warn when a display block returns bare field IDs that likely belong to the opposite
 * collection instead of the current one. display.fields() must return selections relative
 * to the current collection; opposite-collection fields need to be nested under the relation.
 */
function warnIfBareOppositeCollectionFields(options: {
	selection: IdFieldSelection;
	relFieldId: GlobalSingleRelationFieldId;
	field: ResolvedMonospaceSingleRelationField;
	blockId: string;
}) {
	const { selection, relFieldId, field, blockId } = options;
	const bareStrings = selection.filter((entry) => typeof entry === 'string' && entry !== '*') as string[];

	if (bareStrings.length === 0) return;

	// If the only bare string is the relation field ID itself, it's the default expansion sentinel
	if (bareStrings.length === 1 && bareStrings[0] === relFieldId) return;

	// Check if any bare strings are NOT primitives on the current collection
	const currentCollection = field.collection;

	if (!currentCollection) return;

	const currentPrimitiveIds = new Set(currentCollection.primitiveFields?.map((f) => f.id as string));
	const currentRelationIds = new Set(currentCollection.relationFields?.map((f) => f.id as string));
	const unknownFields = bareStrings.filter((id) => !currentPrimitiveIds.has(id) && !currentRelationIds.has(id));

	if (unknownFields.length > 0) {
		console.warn(
			`[display.fields] Block "${blockId}" returned bare field IDs that don't exist on the current collection: ${unknownFields.join(', ')}. `
			+ 'display.fields() must return selections relative to the current collection. '
			+ `Opposite-collection fields should be nested: [{ "${relFieldId}": [...] }]`,
		);
	}
}

/**
 * Process an IdFieldSelection, expanding:
 * - Bare relation field IDs → { relFieldId: resolvedNestedSelection }
 * - IdRelationQuery without fields → auto-resolve fields via display template
 */
function expandBareRelationFieldIds(
	selection: IdFieldSelection,
	collection: ResolvedMonospaceCollection,
	ctx: ResolvedFieldSelectionContext,
	_visited: Set<GlobalCollectionId>,
): IdFieldSelection {
	const result: IdFieldSelection = [];

	for (const entry of selection) {
		// '*' or primitive field ID — pass through
		if (typeof entry === 'string') {
			if (entry === '*') {
				result.push(entry);
				continue;
			}

			// Check if it's a relation field on this collection
			const relationField = collection.relationFields.find((f) => f.id === entry);

			if (relationField) {
				// Bare relation field ID → auto-expand. M2M resolves the {{ toTarget }} template
				// against the junction (so the target nests under the junction's toTarget leg);
				// everything else expands via the opposite collection's display template.
				const nestedSelection = isM2M(relationField)
					? resolveM2MJunctionSelection(relationField, collection.id, ctx, _visited)
					: resolveRelationFieldSelection(
							relationField.oppositeCollectionId,
							collection.id,
							ctx,
							_visited,
						);

				if (nestedSelection) {
					// To-many relations render through DisplayRelatedValues, which only ever
					// shows up to the render cap plus a "cap+" label. Fetch one extra as a
					// sentinel so the listing query stays cheap and never claims an exact total.
					const selection: IdFieldSelection | IdRelationQuery = relationField.isList
						? { fields: nestedSelection, limit: RELATED_VALUES_DROPDOWN_MAX_ITEM_COUNT + 1 }
						: nestedSelection;

					result.push(relationEntry(entry as GlobalSingleRelationFieldId, selection));

					continue;
				}

				// M2M with unresolvable structure (corrupt meta): degrade to the bare-id pass-through
				// below rather than error. Only reachable on malformed schemas, not normal M2M.
			}

			// Primitive field ID — pass through
			result.push(entry as GlobalFieldId);
			continue;
		}

		// Object entry: { relFieldId: IdFieldSelection | IdRelationQuery }
		for (const [key, value] of Object.entries(entry)) {
			const relFieldId = key as GlobalSingleRelationFieldId;

			result.push(relationEntry(relFieldId, value));
		}
	}

	return result;
}

function resolveRelationFieldSelection(
	oppositeCollectionId: GlobalCollectionId,
	currentCollectionId: GlobalCollectionId,
	ctx: ResolvedFieldSelectionContext,
	_visited: Set<GlobalCollectionId>,
): IdFieldSelection {
	const oppositeCollection = ctx.getCollection(oppositeCollectionId);
	if (!oppositeCollection) return ['*'];

	if (_visited.has(oppositeCollection.id)) return ['*'];

	// Try explicit display template first, then fall back to default
	const oppositeDisplayTemplate = oppositeCollection.meta?.displayTemplate
		?? getCollectionDefaultDisplayTemplate(oppositeCollection);

	if (!oppositeDisplayTemplate || !oppositeDisplayTemplate.content || oppositeDisplayTemplate.content.length === 0) {
		return ['*'];
	}

	const nextVisited = new Set(_visited);
	nextVisited.add(currentCollectionId);

	return getDisplayTemplateFieldSelection(
		oppositeDisplayTemplate,
		oppositeCollection,
		ctx,
		nextVisited,
	);
}

function resolveM2MJunctionSelection(
	field: ResolvedMonospaceSingleRelationField,
	sourceCollectionId: GlobalCollectionId,
	ctx: ResolvedFieldSelectionContext,
	_visited: Set<GlobalCollectionId>,
): IdFieldSelection | null {
	let structure: M2MStructure;

	try {
		structure = getM2MStructure(field);
	}
	catch {
		return null;
	}

	const junctionCollection = ctx.getCollection(structure.junction.collectionId);
	if (!junctionCollection) return null;

	// CYCLE GUARD: descent is source → junction → (toTarget) → target. Seed both the source
	// and the junction so a target template referencing the inverse M2M terminates instead of
	// recursing to MAX_DEPTH.
	const visited = new Set(_visited);
	visited.add(sourceCollectionId);
	visited.add(structure.junction.collectionId);

	return getDisplayTemplateFieldSelection(
		getM2MDefaultDisplayTemplate(structure),
		junctionCollection,
		ctx,
		visited,
	);
}

function fieldIdPathToSelection(
	fieldIds: GlobalFieldId[],
	collection: ResolvedMonospaceCollection,
	ctx: ResolvedFieldSelectionContext,
	_visited: Set<GlobalCollectionId>,
): IdFieldSelection[number] | null {
	const [fieldId, ...rest] = fieldIds;
	if (!fieldId) return null;

	const field = findCollectionFieldById(collection, fieldId);
	if (!field) return null;

	if (isPrimitiveField(field)) {
		return rest.length === 0 ? field.id : null;
	}

	const relFieldId = field.id as GlobalSingleRelationFieldId;

	if (rest.length === 0) {
		const relationSelection = resolveFieldDisplayFieldSelectionRecursive(field, collection, ctx, _visited);
		const selfEntry = relationSelection.length === 1 && typeof relationSelection[0] === 'object'
			? relationSelection[0]?.[relFieldId]
			: undefined;

		return relationEntry(relFieldId, selfEntry ?? relationSelection);
	}

	const oppositeCollection = field.oppositeCollection ?? ctx.getCollection(field.oppositeCollectionId);
	if (!oppositeCollection) return null;

	const nestedSelection = fieldIdPathToSelection(rest, oppositeCollection, ctx, _visited);
	if (!nestedSelection) return null;

	return relationEntry(relFieldId, [nestedSelection]);
}

export function resolveCollectionDisplayTemplateFields(
	collection: ResolvedMonospaceCollection,
	ctx: ResolvedFieldSelectionContext,
	displayTemplate?: DisplayTemplateAst,
): IdFieldSelection {
	const ast = displayTemplate
		?? collection.meta?.displayTemplate
		?? getCollectionDefaultDisplayTemplate(collection);

	if (!ast?.content?.length) return ['*'];

	return getDisplayTemplateFieldSelection(ast, collection, ctx);
}

/**
 * Resolve the `IdFieldSelection` for one display-template field-path ref, so
 * callers can memoize query fields per visible path.
 */
export function resolveFieldPathSelection(
	collection: ResolvedMonospaceCollection,
	fieldPath: GlobalFieldId[],
	ctx: ResolvedFieldSelectionContext,
): IdFieldSelection {
	if (fieldPath.length === 0) return [];

	if (fieldPath.length > 1) {
		const selection = fieldIdPathToSelection(fieldPath, collection, ctx, new Set());
		return selection ? [selection] : [];
	}

	const field = findCollectionFieldById(collection, fieldPath[0]!);
	if (!field) return [];

	return resolveFieldDisplayFieldSelectionRecursive(field, collection, ctx, new Set());
}
